/**
 * The edge HTTP contract: the header names a browser page exchanges with the
 * API gateway cross-origin.
 *
 * A cross-origin fetch reaches the gateway only if every request header it
 * sets is CORS-safelisted or listed in the preflight's
 * Access-Control-Allow-Headers, and the page can read only the response
 * headers that are CORS-safelisted or listed in Access-Control-Expose-Headers.
 * Both lists used to be string literals copied inside the gateway, so a header
 * added on one side failed only in a real browser: X-Agreement-Session was
 * never allowed, which blocked every agreement-link call after the code step.
 *
 * These lists are the one source. The gateway's CORS middleware
 * (apps/vhire-gateway/src/middleware/cors.ts) serves them, the frontend and
 * billing name headers through them, and a gateway contract test
 * (tests/contract/cors-browser-headers.test.ts) fails when the frontend sends
 * or reads a header outside them. Adding a header means adding it here.
 *
 * Header names are case-insensitive on the wire: compare through
 * headerNameSet(), never with ===.
 */

/** The post-OTP agreement session: minted by billing, held in memory by the
 *  signer page and sent on every agreement call after the code step. */
export const AGREEMENT_SESSION_HEADER = 'X-Agreement-Session';

/** Request headers a browser page may send the gateway cross-origin
 *  (Access-Control-Allow-Headers). */
export const BROWSER_REQUEST_HEADERS: readonly string[] = [
  'Content-Type',
  'Authorization',
  'x-correlation-id',
  'x-tenant-id',
  'x-auth-token',
  'x-candidate-id',
  'X-Session-Token',
  'X-VHire-Location-Purpose',
  'cache-control',
  'x-requested-with',
  'accept',
  'accept-language',
  AGREEMENT_SESSION_HEADER,
];

/** The entitlement access gate's mode (off | shadow | enforce), sent on
 *  GET /api/tenants/:id/package so the workspace banner says "read only" only
 *  when the gateway enforces it (entitlements PR2A, interface I13). */
export const ACCESS_GATE_HEADER = 'x-vhire-access-gate';

/** Response headers a browser page may read beyond the safelist
 *  (Access-Control-Expose-Headers): the download name and size, the agent
 *  chat's conversation id, the request id for support, the back-off, and the
 *  access gate mode. */
export const EXPOSED_RESPONSE_HEADERS: readonly string[] = [
  'Content-Disposition',
  'Content-Length',
  'X-Conversation-Id',
  'X-Request-Id',
  'X-Offer-Template-Page',
  'Retry-After',
  ACCESS_GATE_HEADER,
];

/** Fetch-standard CORS-safelisted request headers that need no preflight
 *  entry whatever their value. Content-Type and Range are safelisted only for
 *  some values (application/json is not one), so they are not here; the app's
 *  Content-Type is in BROWSER_REQUEST_HEADERS instead. */
export const CORS_SAFELISTED_REQUEST_HEADERS: readonly string[] = ['Accept', 'Accept-Language', 'Content-Language'];

/** Fetch-standard CORS-safelisted response headers: readable cross-origin
 *  without being exposed. */
export const CORS_SAFELISTED_RESPONSE_HEADERS: readonly string[] = [
  'Cache-Control',
  'Content-Language',
  'Content-Length',
  'Content-Type',
  'Expires',
  'Last-Modified',
  'Pragma',
];

/** Lower-cased union of header-name lists, for case-insensitive membership. */
export function headerNameSet(...lists: ReadonlyArray<readonly string[]>): Set<string> {
  return new Set(lists.flat().map((name) => name.toLowerCase()));
}

/** Stem of an agreement download name when the document number is unknown. */
export const AGREEMENT_FILE_FALLBACK_STEM = 'agreement';

/**
 * The download name of an agreement item: `<documentNumber>-<itemKey>.pdf`,
 * or `agreement-<itemKey>.pdf` when the number is unknown. The gateway sends it
 * in Content-Disposition; the frontend builds its own fallback the same way.
 */
export function agreementFileName(documentNumber: unknown, itemKey: string): string {
  const stem = typeof documentNumber === 'string' && documentNumber ? documentNumber : AGREEMENT_FILE_FALLBACK_STEM;
  return `${stem}-${itemKey}.pdf`;
}

/** True for the generic name: whoever named the file did not know the
 *  document number (a gateway deployed ahead of the billing that sends it). */
export function isFallbackAgreementFileName(name: string): boolean {
  return name.startsWith(`${AGREEMENT_FILE_FALLBACK_STEM}-`);
}
